import { useCallback, useEffect, useRef, useState } from "react";

export type AutoSaveStatus = "idle" | "saving" | "saved" | "error";

interface UseAutoSaveOptions {
  data: any;
  onSave: () => Promise<void>;
  debounceMs?: number;
  enabled?: boolean;
  onError?: (error: Error) => void;
}

interface UseAutoSaveReturn {
  status: AutoSaveStatus;
  lastSaved: Date | null;
  saveNow: () => Promise<void>;
}

export function useAutoSave({
  data,
  onSave,
  debounceMs = 1500,
  enabled = true,
  onError,
}: UseAutoSaveOptions): UseAutoSaveReturn {
  const [status, setStatus] = useState<AutoSaveStatus>("idle");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  const lastSavedDataRef = useRef<string | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const dataRef = useRef(data);
  const onSaveRef = useRef(onSave);
  const enabledRef = useRef(enabled);
  const performSaveRef = useRef<() => Promise<void>>(async () => {});

  dataRef.current = data;
  onSaveRef.current = onSave;
  enabledRef.current = enabled;

  const serializedData = JSON.stringify(data);

  const performSave = useCallback(async () => {
    const snapshotAtSave = JSON.stringify(dataRef.current);

    try {
      if (isMountedRef.current) {
        setStatus("saving");
      }
      await onSaveRef.current();
      lastSavedDataRef.current = snapshotAtSave;

      if (isMountedRef.current) {
        setStatus("saved");
        setLastSaved(new Date());

        setTimeout(() => {
          if (isMountedRef.current) {
            setStatus("idle");
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Auto-save failed:", error);
      if (isMountedRef.current) {
        setStatus("error");
        if (onError) {
          onError(error as Error);
        }
      }
    }
  }, [onError]);

  performSaveRef.current = performSave;

  const saveNow = useCallback(async () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    await performSave();
  }, [performSave]);

  useEffect(() => {
    if (
      !enabled ||
      lastSavedDataRef.current === serializedData ||
      !data ||
      Object.keys(data).length === 0
    ) {
      return undefined;
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      performSave();
    }, debounceMs);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, [serializedData, enabled, debounceMs, performSave, data]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }

      const current = JSON.stringify(dataRef.current);
      const hasUnsaved =
        enabledRef.current &&
        lastSavedDataRef.current !== current &&
        dataRef.current &&
        Object.keys(dataRef.current).length > 0;

      if (hasUnsaved) {
        void performSaveRef.current();
      }

      isMountedRef.current = false;
    };
  }, []);

  return {
    status,
    lastSaved,
    saveNow,
  };
}
